import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react';
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/react';
import {
  ChevronUpDownIcon,
  CheckIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/solid';

import { useTranslation } from 'react-i18next';

/**
 * Represents an option in the select dropdown
 */
export interface SelectOption {
  /**
   * Unique identifier/value for the option
   */
  value: string;
  /**
   * Display content for the option. If not provided, value will be used
   */
  label?: ReactNode;
}

/**
 * Props for the Select component
 * @template T - Type extending SelectOption
 */
export interface SelectProps<T extends SelectOption> {
  /**
   * The options in the dropdown for selection.
   * A SelectOption must have a value
   * i.e. [{ value: "young" }, { value: "athletic" }]
   */
  options: T[];

  /**
   * Currently selected option's value (for controlled mode)
   */
  value?: string;

  /**
   * The initial selected value (for uncontrolled mode)
   */
  defaultValue?: string;

  /**
   * Callback function triggered when an option is selected
   * @param selected - The entire selected option object
   */
  onSelect?: (selected: T) => void;

  /**
   * Text to display when no option is selected
   */
  placeholder?: string;

  /**
   * Whether the select component is disabled
   */
  disabled?: boolean;

  /**
   * When true, options are rendered in a portal and positioned with Floating UI so the dropdown
   * is not clipped by ancestor overflow. Use inside scrollable areas (e.g. Environments page).
   */
  isFixedPositionOptions?: boolean;

  /**
   * Label that is displayed inside the component before the selected value
   */
  label?: ReactNode;

  /**
   * Whether the select component should include search functionality
   */
  isSearchable?: boolean;

  /**
   * Additional classes to apply to the component, e.g. `'absolute top-0 right-full'`
   */
  className?: string;

  /**
   * The menu's width: the button's (the default), or as wide as its longest
   * option and never narrower than the button.
   */
  menuWidth?: 'button' | 'content';

  /**
   * Which of the button's edges the menu lines up with. `end`, for a select
   * at the right of the page, keeps a wider menu from running off it.
   */
  menuAlign?: 'start' | 'end';

  /**
   * With `isSearchable`, lets the search text be picked when no option has
   * exactly that value: a last row, labelled by this, selects
   * `{ value: text }` as an option would. For values the list may not hold,
   * such as a tag key seen on few assets.
   */
  createLabel?: (text: string) => ReactNode;
  'data-testid'?: string;
}

/**
 * A customizable select/dropdown component that supports both controlled and uncontrolled modes.
 *
 * @example
 * ```tsx
 * const options = [
 *   { value: 'option1', label: 'Option 1' },
 *   { value: 'option2', label: 'Option 2' }
 * ];
 *
 * // Uncontrolled
 * <Select options={options} defaultValue="option1" onSelect={(selected) => console.log(selected)} />
 *
 * // Controlled
 * <Select options={options} value={selectedValue} onSelect={handleSelect} />
 * ```
 */
export const Select = <T extends SelectOption>({
  options,
  placeholder,
  onSelect,
  disabled = false,
  'data-testid': dataTestId,
  value,
  defaultValue,
  label,
  className = '',
  isFixedPositionOptions = false,
  isSearchable = false,
  menuWidth = 'button',
  menuAlign = 'start',
  createLabel,
}: SelectProps<T>) => {
  const { t } = useTranslation('designSystem');
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue);
  const [query, setQuery] = useState('');

  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-start',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [offset(4), shift({ padding: 8 }), flip()],
  });

  const selectedValue = useMemo(
    () =>
      options.find(
        (option) => option.value === (isControlled ? value : internalValue),
      ),
    [options, value, internalValue, isControlled],
  );

  // Filtered options based on search query
  const filteredOptions = useMemo(() => {
    if (!isSearchable || query === '') {
      return options;
    }
    const lowercaseQuery = query.toLowerCase();
    return options.filter((option) => {
      if (option.label && typeof option.label === 'string') {
        return option.label.toLowerCase().includes(lowercaseQuery);
      }
      return option.value.toLowerCase().includes(lowercaseQuery);
    });
  }, [query, options, isSearchable]);

  // The search text as an option of its own, when allowed and not listed.
  const typed = query.trim();
  const created =
    isSearchable &&
    createLabel &&
    typed &&
    !options.some((option) => option.value === typed)
      ? ({ value: typed } as T)
      : undefined;

  const handleChange = useCallback(
    (item: T | null) => {
      // This interface is broken. Combobox allows for null values, but the upstream onSelect does not.
      if (!item) {
        return;
      }
      if (!isControlled) {
        setInternalValue(item.value);
      }
      onSelect?.(item);
    },
    [isControlled, onSelect],
  );

  // Validate props in development
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      if (defaultValue !== undefined && value !== undefined) {
        // eslint-disable-next-line no-console
        console.warn(
          'Select component received both `value` and `defaultValue` props. ' +
            '`value` will take precedence as the component will be treated as controlled.',
        );
      }
    }
  }, [defaultValue, value]);

  return (
    <Combobox
      as="div"
      className={`relative w-auto ${className}`}
      value={selectedValue}
      onChange={handleChange}
      disabled={disabled}
      onClose={() => setQuery('')}
    >
      {({ open }) => {
        const buttonWidth =
          isFixedPositionOptions && refs.reference.current
            ? refs.reference.current.getBoundingClientRect().width
            : undefined;

        const optionsContent = (
          <>
            {isSearchable && (
              <div className="relative p-2">
                <MagnifyingGlassIcon className="absolute top-1/2 left-4 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                <ComboboxInput
                  type="search"
                  as="input"
                  placeholder={t('searchLabel')}
                  autoFocus
                  className="w-full rounded-md border border-gray-300 bg-white py-[6px] pr-[15px] pl-8 text-sm font-medium focus:border-gray-300 focus:ring-0 focus:outline-none dark:border-gray-600 dark:bg-gray-900 dark:focus:border-gray-600"
                  onChange={(event) => setQuery(event.target.value)}
                  displayValue={() => query}
                />
              </div>
            )}
            <div className="max-h-60 overflow-auto">
              {filteredOptions.map((option) => (
                <ComboboxOption
                  key={option.value}
                  className="data-[focus]:bg-action-primary/10 data-[selected]:bg-action-primary/10 m-1 flex cursor-pointer items-center justify-between rounded-md px-[15px] py-[7px] text-sm font-medium select-none"
                  value={option}
                >
                  {() => {
                    const isSelected = selectedValue?.value === option.value;
                    return (
                      <>
                        <span
                          className={`truncate ${isSelected ? 'text-action-primary' : 'text-darkest-gray'}`}
                          title={getTooltipTitle(option)}
                        >
                          {isSearchable && query
                            ? highlightMatch(
                                typeof option.label === 'string'
                                  ? option.label
                                  : option.value,
                                query,
                              )
                            : option.label || option.value}
                        </span>
                        {isSelected && (
                          <CheckIcon className="text-action-primary h-4 w-4" />
                        )}
                      </>
                    );
                  }}
                </ComboboxOption>
              ))}
              {created && createLabel && (
                <ComboboxOption
                  key={`create:${created.value}`}
                  className="data-[focus]:bg-action-primary/10 text-darkest-gray m-1 flex cursor-pointer items-center rounded-md px-[15px] py-[7px] text-sm font-medium select-none"
                  value={created}
                  data-testid="select-create-option"
                >
                  {createLabel(created.value)}
                </ComboboxOption>
              )}
            </div>
          </>
        );

        return (
          <>
            <ComboboxButton
              ref={isFixedPositionOptions ? refs.setReference : undefined}
              className={`px-sm py-xs data-focus:ring-action-primary data-[disabled]:text-darker-gray relative flex w-full cursor-default items-center rounded-xl border-2 bg-white text-left text-sm font-medium data-focus:ring-2 data-focus:ring-offset-2 data-focus:outline-none data-[disabled]:cursor-not-allowed data-[disabled]:bg-gray-100 dark:bg-gray-900 dark:data-[disabled]:bg-gray-800 ${open ? 'border-action-primary' : ''}`}
              data-testid={dataTestId}
            >
              {label && (
                <div className="flex shrink-0 font-semibold">{label}</div>
              )}
              <div
                className="min-w-0 flex-1 truncate"
                data-testid="select-value"
              >
                {selectedValue ? (
                  <span
                    className="truncate"
                    title={getTooltipTitle(selectedValue)}
                  >
                    {selectedValue.label || selectedValue.value}
                  </span>
                ) : (
                  <span className="text-darker-gray">
                    {placeholder || <>&nbsp;</>}
                  </span>
                )}
              </div>
              {/* Dropdown Icon */}
              {/* TODO: check for the icon color changes */}
              <div className="pointer-events-none h-full shrink-0 items-center">
                <ChevronUpDownIcon
                  className={`-my-2 -mr-1 ml-2 h-3.5 w-3.5 ${
                    selectedValue ? 'text-darker-gray' : 'text-darkest-gray'
                  } `}
                  aria-hidden="true"
                />
              </div>
            </ComboboxButton>
            {isFixedPositionOptions && open ? (
              createPortal(
                <div
                  ref={refs.setFloating}
                  style={{
                    ...floatingStyles,
                    zIndex: 120,
                    ...(typeof buttonWidth === 'number'
                      ? { width: buttonWidth }
                      : {}),
                  }}
                  className="rounded-xl bg-white shadow-lg dark:bg-gray-900"
                >
                  <ComboboxOptions
                    as="div"
                    modal={false}
                    transition
                    hold={isSearchable && query !== ''}
                    className="w-full rounded-xl"
                  >
                    {optionsContent}
                  </ComboboxOptions>
                </div>,
                document.body,
              )
            ) : !isFixedPositionOptions ? (
              <ComboboxOptions
                as="div"
                modal={false}
                transition
                hold={isSearchable && query !== ''}
                className={`absolute z-120 mt-1 rounded-xl bg-white shadow-lg dark:bg-gray-900 ${
                  menuWidth === 'content'
                    ? 'w-max min-w-[var(--button-width)]'
                    : 'w-[var(--button-width)]'
                } ${menuAlign === 'end' ? 'right-0' : ''}`}
              >
                {optionsContent}
              </ComboboxOptions>
            ) : null}
          </>
        );
      }}
    </Combobox>
  );
};

/**
 * Highlights matching text within a string
 * @param text - The text to highlight
 * @param query - The search query to highlight
 * @returns JSX with highlighted matches
 */
const highlightMatch = (text: string, query: string): ReactNode => {
  if (!query.trim()) {
    return text;
  }

  const regex = new RegExp(
    `(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`,
    'gi',
  );
  const parts = text.split(regex);

  return parts.map((part, index) => {
    if (part.toLowerCase() === query.toLowerCase()) {
      return (
        <span key={index} className="text-action-primary font-medium">
          {part}
        </span>
      );
    }
    return part;
  });
};

/**
 * TODO: option.label could be a ReactNode, Need to handle that case and move this duplicate code
 * Get the display title for an option
 * @param option - The option to get the display title for
 * @returns The display title
 */
const getTooltipTitle = ({ label, value }: SelectOption) =>
  typeof label === 'string' ? label : value;
